/**
 * 星球上的新鲜事（观察室的实时事件流）：GET /public/world/feed?before=&limit=。
 * 类型用契约的正式类型（feed v4，generated.ts 87588f92：PublicWorldFeed / PublicFeedItem / PublicArea / PublicFeedKind），和第一稿字段一样；
 * v4 起 area.name 是片区级的叫法（例如「中环一带（香港）」，离片区都远时只给城市名），text 不含店名、驿站名。
 * - kind 是封闭集合：outing_started、event_joined、arrived、came_home、photo_posted、met；
 * - text 后端已经写好、不露内部码，**前端优先用它**；text 万一是空的，才按 kind 和名字补一句（名字从地图图层按 pet_id 查，查不到不编代称）；
 * - area 只到片区（格子中心 + 片区叫法 + 城市）；相遇（met）恒为 null；
 * - photo 仅消费 P114 返回的具名居民生活照片公开读口，主体由正规 URL 绑定；拍摄/发布时间分开，原来源事件可不在本页。旧明信片/私相册不恢复。
 * 页面不编事件：读不到就说读不到，没有就说这会儿很安静。
 */
import type { PublicArea, PublicFeedItem, PublicFeedKind, PublicWorldFeed } from "@/shared/contracts";
import type { ApiClient } from "@/shared/api/client";

export type WorldFeed = PublicWorldFeed;
export type WorldFeedItem = PublicFeedItem;
export type WorldFeedKind = PublicFeedKind;

export interface WorldFeedReader {
  /** 最新一页（不带游标；轮询只看最新的） */
  feed(signal?: AbortSignal): Promise<WorldFeed>;
}

/** 一页取多少条（后端默认 30、上限 50） */
export const FEED_PAGE_LIMIT = 30;

export function liveWorldFeedReader(api: ApiClient): WorldFeedReader {
  return { feed: (signal) => api.request<WorldFeed>("/public/world/feed", { query: { limit: FEED_PAGE_LIMIT }, signal }) };
}

/** 演示模式没有事件流：给空的（如实说“这会儿很安静”），不编事件。 */
export function demoWorldFeedReader(): WorldFeedReader {
  return { feed: async () => ({ server_time: new Date().toISOString(), items: [], next_cursor: null }) };
}

/** 轮询间隔：30 秒；页面不在前台时不轮询（React Query 的 refetchIntervalInBackground 默认关）。 */
export const FEED_POLL_MS = 30_000;

/**
 * 一条事件 → 一句中文：后端的 text 优先；空的才补（不露 kind 等内部码，名字查不到就不写人名、也不用“一位”这类代称）。
 */
export function eventText(item: WorldFeedItem, nameOf: (petId: string) => string | null = () => null): string {
  const text = item.text?.trim();
  if (text) return text;
  const names = item.pet_ids.map(nameOf).filter((name): name is string => Boolean(name));
  const who = names[0];
  if (!who) return "星球上有一条新动态";
  switch (item.kind) {
    case "outing_started":
      return `${who}出门了`;
    case "event_joined":
      return `${who}出发去参加星球活动`;
    case "arrived":
      return item.area?.name ? `${who}到了${item.area.name}` : `${who}到了要去的地方`;
    case "came_home":
      return `${who}回到驿站了`;
    case "photo_posted":
      return `${who}分享了一张照片`;
    case "met":
      return names.length >= 2 ? `${names[0]}和${names[1]}遇见了` : "星球上有一条新动态";
    default:
      return "星球上有一条新动态";
  }
}

/**
 * 发生在哪一带（只到片区）的叫法。v4 的 name 已经是片区说法（「中环一带（香港）」）就原样用；
 * 离片区都远时 name 只有城市名（「香港」），补成「香港一带」；没有 name 用城市；都没有说「这一带」。
 */
export function areaText(area: PublicArea): string {
  const name = area.name?.trim();
  if (name) return name.includes("一带") ? name : `${name}一带`;
  const city = area.city?.trim();
  return city ? `${city}一带` : "这一带";
}

// 星球上要闪的点改由 6796 的 planet3d/feedToGlobe 算（第六轮）：只闪看着的时候新来的（已见集合、首读只记不闪），时刻用页面拿到的时刻。
